import React, { Component } from 'react';
import PropTypes from 'prop-types';
import i18next from 'i18next';
import classNames from 'classnames';
import get from 'lodash.get';

import { PaidContent } from 'components/PaidContent';
import Ad from 'components/Ad';
import Link from 'components/Link';
import TeasePicture from 'components/TeasePicture';
import TypeIcon from 'components/TypeIcon';
import Unibrow from 'components/Unibrow';

import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { imageAltForItem } from 'lib/imageAlt';
import { package as PackagePropType } from 'lib/CustomPropTypes';
import { TEASE_LIST_ALTERNATE_ITEM_DISPLAY } from 'lib/brandFeatures.json';
import { teaseList as paidContent } from 'lib/paidContentUtils';
import { timeFrom } from 'lib/DateTime';
import AIMS_FLAVORS from 'lib/aimsFlavors';

import { SeeAllButton } from './SeeAllButton';
import { TeaseListContainer, TeaseListItemContainer } from './containers';

class TeaseList extends Component {
  static propTypes = {
    content: PackagePropType.isRequired,
    vertical: PropTypes.string.isRequired,
    route: PropTypes.string,
    pkgClassName: PropTypes.string,
  };

  static defaultProps = {
    route: '/',
    pkgClassName: 'pkg teaseList',
  };

  constructor(props) {
    super(props);

    const { content: { items } } = props;

    this.state = {
      items,
      boxAdLoaded: false,
      paidContentLoaded: false,
      topHeight: 0,
    };
  }

  componentDidMount() {
    let { content: { items } } = this.props;
    // On first render, insert paid content in-between 3rd and 4th items
    if (items.length >= 3) {
      items = [...items.slice(0, 3), paidContent, ...items.slice(3)];
    }
    this.setState({ items });
  }

  renderArticleTeaseListItemContainer = (article) => {
    const { paidContentLoaded } = this.state;
    const { content: { metadata: packageMetadata }, vertical } = this.props;
    const {
      id, item, computedValues, metadata: contentMetadata, type,
    } = article;

    // eslint-disable-next-line max-len
    const alternateTeaseListDisplay = getFeatureConfigForBrand(TEASE_LIST_ALTERNATE_ITEM_DISPLAY, vertical);

    if (type === paidContent.type) {
      const isNewsOrMsnbc = vertical === 'news' || vertical === 'msnbc';
      const toolkitStyling = (isNewsOrMsnbc && ['pl5', 'pt5', 'pb5', 'pl7-m', 'pt7-m', 'pb7-m', 'pl5-l', 'pt5-l', 'pb5-l']) || '';
      return (
        <PaidContent
          {...article}
          className={classNames(toolkitStyling,
            'relative',
            'tease-list__article',
            {
              'tease-list__article--hidden': !paidContentLoaded,
            })}
          key={paidContent.type}
          adLoaded={this.setPaidContentLoaded}
          vertical={vertical}
        />
      );
    }

    if (!computedValues) return null;

    const {
      headline,
      url,
      teaseImage,
      unibrow,
      alternateTeaseImages = [],
    } = computedValues;


    const isAd = get(item, 'nativeAd');

    let eyebrowClass = 'tease-list__eyebrow';
    if (isAd) {
      eyebrowClass = alternateTeaseListDisplay ? 'tease-list__trending-paid' : 'tease-list__paid';
    }

    const metadata = {
      ...packageMetadata,
      ...contentMetadata,
    };

    // Default for showTeaseImage on TeaseList is false
    const showTeaseImage = metadata.showTeaseImage && teaseImage;

    const showTimestamp = !alternateTeaseListDisplay && !isAd && !!item;
    const showDot = !isAd && !alternateTeaseListDisplay;

    // TODO: properly align all variations of content next to teaseImage when shown
    // Hack below adds &nbsp; so that headline is properly aligned when no unibrow.text set
    const showEyebrow = alternateTeaseListDisplay && (isAd || unibrow.text || showTeaseImage);

    const eyebrowToolkitClasses = 'f2 lh-none founders-mono fw1 mb2';

    const itemClasses = [
      'tease-list__article',
      {
        'tease-list__article--show-dot': showDot,
        'tease-list__article--align-dot-to-hed': showDot && !showTimestamp, // dot is aligned center if no timestamp
      }];

    if (alternateTeaseListDisplay) {
      itemClasses.push('pv5 ph0-l');
    } else {
      itemClasses.push('mt5 mt7-m mt5-l'); // top ( must be margin for dot positioning)
      itemClasses.push('pb5 pb7-m pb5-l'); // bottom
      itemClasses.push('pl5 pl7-m pl5-l'); // left
    }

    const teaseImageWith1X1AspectRatio = alternateTeaseImages.find(({ aspectRatio }) => aspectRatio === 'ASPECT_1X1');
    const teaseImageWith1X1AspectRatioURL = get(teaseImageWith1X1AspectRatio, 'image.url.primary', '');
    const teaseImageURL = get(teaseImage, 'url.primary', '');

    return (
      <TeaseListItemContainer key={id} itemId={id} itemClasses={classNames(itemClasses)}>
        {alternateTeaseListDisplay && showTeaseImage && (
          <Link to={url}>
            <div
              className={classNames('tease-list__tease-picture', 'relative h-100')}
            >
              <TeasePicture
                className="tease-list__image"
                url={teaseImageWith1X1AspectRatioURL || teaseImageURL}
                responsiveFlavors={{
                  s: AIMS_FLAVORS.FOCAL_80x80,
                }}
                alt={imageAltForItem(article)}
                retina
              />
              <TypeIcon type={type} className="tease-list__type-icon" />
            </div>
          </Link>
        )}

        <div className="tease-list__info">
          {isAd && <Ad slot="native" />}

          {showTimestamp
            && (
              <div className={classNames('tease-list__timestamp', eyebrowToolkitClasses)}>
                {i18next.t('time_ago', { time: timeFrom(item.dateCreated) })}
              </div>
            )}

          {showEyebrow
            && (
              <Unibrow
                className={classNames(eyebrowClass, eyebrowToolkitClasses, 'ttu')}
                unibrow={({
                  ...unibrow,
                  text: unibrow.text || <span>&nbsp;</span>,
                  override: isAd ? i18next.t('Paid Content') : null,
                })}
              />
            )}

          <h2 className="tease-list__title">
            <Link to={url}>{headline}</Link>
          </h2>

          {isAd && item.source.organization
            && <p className="tease-list__ad-source">{item.source.organization.name.toUpperCase()}</p>}

          {!alternateTeaseListDisplay
            && showTeaseImage
            && (
              <Link to={url} className="tease-list__tease-picture">
                <TeasePicture
                  url={teaseImage.url.primary}
                  responsiveFlavors={{
                    s: AIMS_FLAVORS.FOCAL_760x380,
                    m: AIMS_FLAVORS.FOCAL_1000x500,
                    l: AIMS_FLAVORS.FOCAL_260x130,
                  }}
                  alt={imageAltForItem(article)}
                />
              </Link>
            )}
        </div>
      </TeaseListItemContainer>
    );
  };

  // Return the article list markup
  renderTeaseListItems = () => {
    const { route, vertical } = this.props;
    const { items } = this.state;

    const isToday = vertical === 'today';
    const isCover = route === '/';

    const limitTo7Items = (!isCover || (isToday && isCover));
    const classes = classNames(
      'tease-list__articles',
      'ph5 ph0-m',
      {
        'tease-list__item-limit7': limitTo7Items,
      },
    );

    const wrapperClasses = classNames('tease-list__articles-wrapper',
      {
        'tease-list__articles-wrapper--limit7': limitTo7Items,
      });

    return (
      <div className={wrapperClasses}>
        <ul className={classes}>
          {items.filter((item) => item).map(this.renderArticleTeaseListItemContainer)}
        </ul>
      </div>
    );
  };

  renderBoxAd = () => {
    const { boxAdLoaded } = this.state;
    const adAboveList = get(this.props, 'content.adAboveList', false);

    const slot = get(this.props, 'content.adType');

    if (!slot) return null;

    const offsetViewport = slot === 'boxrail' || slot === 'boxflex'
      ? 100
      : 50;

    const adClasses = classNames(
      'ad',
      {
        mb8: boxAdLoaded && adAboveList,
        mt8: boxAdLoaded && !adAboveList,
      },
    );

    return (
      <div className={adClasses} data-test="teaseListAd">
        <Ad
          slot={slot}
          packageId={get(this.props, 'content.id')}
          offsetViewport={offsetViewport}
          handler={(enigmaAd, { EVENTS }) => {
            enigmaAd.on(EVENTS.RENDER, (event) => {
              if (!event || event.empty) {
                return;
              }
              this.setState((prevState) => (
                // TODO: Magic number 300 should be pulled from boxAd height definition
                { topHeight: prevState.topHeight + 300, boxAdLoaded: true }
              ));
            });
          }}
        />
      </div>
    );
  };

  renderAd = () => {
    const {
      content: { id, adType },
    } = this.props;

    return (
      adType
      && this.renderBoxAd({
        slot: adType,
        packageId: id,
        offsetViewport: 50,
      })
    );
  };

  setPaidContentLoaded = (loaded) => {
    this.setState({ paidContentLoaded: loaded });
  };

  renderSeeAllButton = () => {
    const { content } = this.props;
    const {
      metadata: { seeAllUrl, seeAllText },
    } = content;

    return (
      <SeeAllButton seeAllUrl={seeAllUrl} seeAllText={seeAllText} />
    );
  }

  render() {
    return (
      <TeaseListContainer
        {...this.props}
        ad={this.renderAd()}
      >
        <div className="tease-list__articles-and-button">
          <div className="tease-list__stripe-pattern" />
          {this.renderTeaseListItems()}
          {this.renderSeeAllButton()}
        </div>
      </TeaseListContainer>
    );
  }
}

export default TeaseList;
